import { useQuery } from '@tanstack/react-query';
import { GenerationHelp } from './GenerationHelp.js';
import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import { notify } from '@/lib/notify.js';
import { captureApiRequestAuthority } from '@/lib/api-request-authority.js';
import { localStoreWorkspaceId } from './local-suite.js';

import { Label } from '@/components/ui/label.js';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import {
  localSuitesQueryOptions,
  projectQueryOptions,
  useCreateLocalSuite,
  testRailSectionsQueryOptions,
  testSuitesQueryOptions,
  xrayFoldersQueryOptions,
  zephyrFoldersQueryOptions,
} from '@/lib/api/quality.js';
import {
  runOutputSource,
  type GeneratedCasePublicationBatch,
  type GeneratedCasePublicationDestination,
  type Run,
  type Project,
  type RunProposal,
  type TestSuite,
} from '@/lib/api/quality-types.js';

import { orderZephyrFolders } from './zephyr-folder-tree.js';
import { ConnectorBadge } from './connectors/ConnectorBadge.js';
import { connectorForSource } from './connectors/connector-status.js';

const NONE = '__none__';
const ROOT = '__root__';

export function isCompletePublicationDestination(
  destination: GeneratedCasePublicationDestination | null,
): destination is GeneratedCasePublicationDestination {
  if (!destination) return false;
  if (destination.provider === 'own-tms') {
    return Boolean(destination.suiteId?.trim());
  }
  if (destination.provider === 'azure') {
    return Boolean(
      destination.project.trim() &&
      destination.planId.trim() &&
      destination.suiteId.trim(),
    );
  }
  if (destination.provider === 'testrail') {
    return (
      destination.projectId.trim().length > 0 &&
      Number.isSafeInteger(destination.sectionId)
    );
  }
  if (destination.projectKey.trim().length === 0) return false;
  if (destination.provider === 'zephyr') return true;
  if (destination.testSetId !== undefined) {
    return Boolean(destination.testSetId?.trim());
  }
  if (destination.testPlanId !== undefined) {
    return Boolean(destination.testPlanId?.trim());
  }
  if (destination.folderPath !== undefined) {
    return (
      destination.folderPath === null || destination.folderPath.trim() !== ''
    );
  }
  return false;
}

function fromBatch(
  batch: GeneratedCasePublicationBatch,
): GeneratedCasePublicationDestination {
  const destination = batch.destination;
  if (destination.provider === 'own-tms') {
    return {
      provider: 'own-tms',
      suiteId: destination.suite_id,
      ...(destination.suite_name ? { suiteName: destination.suite_name } : {}),
    };
  }
  if (destination.provider === 'azure') {
    return {
      provider: 'azure',
      project: destination.project,
      planId: destination.plan_id,
      suiteId: destination.suite_id,
      ...(destination.plan_name ? { planName: destination.plan_name } : {}),
      ...(destination.suite_name ? { suiteName: destination.suite_name } : {}),
    };
  }
  if (destination.provider === 'testrail') {
    return {
      provider: 'testrail',
      projectId: destination.project_id,
      sectionId: destination.section_id,
      ...(destination.section_name
        ? { sectionName: destination.section_name }
        : {}),
    };
  }
  if (destination.provider === 'zephyr') {
    return {
      provider: 'zephyr',
      projectKey: destination.project_key,
      folderId: destination.folder_id,
      ...(destination.folder_name
        ? { folderName: destination.folder_name }
        : {}),
    };
  }
  if (destination.test_set_id) {
    return {
      provider: 'xray',
      projectKey: destination.project_key,
      testSetId: destination.test_set_id,
      ...(destination.label ? { label: destination.label } : {}),
    };
  }
  if (destination.test_plan_id) {
    return {
      provider: 'xray',
      projectKey: destination.project_key,
      testPlanId: destination.test_plan_id,
      ...(destination.label ? { label: destination.label } : {}),
    };
  }
  return {
    provider: 'xray',
    projectKey: destination.project_key,
    folderPath: destination.folder_path,
    ...(destination.label ? { label: destination.label } : {}),
  };
}

/** An older service sends a stored-suite batch without its suite id; such a batch names no target. */
function hasUsableDestination(batch: GeneratedCasePublicationBatch): boolean {
  return (
    batch.destination.provider !== 'own-tms' ||
    Boolean(batch.destination.suite_id)
  );
}

/** Uses the latest successful batch, then the launch destination, as the next batch default. */
export function defaultPublicationDestination(
  run: Run,
  proposal?: RunProposal,
): GeneratedCasePublicationDestination | null {
  const latestSuccessful = [...(proposal?.batches ?? [])]
    .reverse()
    .find(
      (batch) =>
        (batch.status === 'succeeded' || batch.status === 'partial') &&
        hasUsableDestination(batch),
    );
  if (latestSuccessful) return fromBatch(latestSuccessful);

  const provider = runOutputSource(run);
  if (provider === 'own-tms' && run.local_output_suite_id) {
    return { provider, suiteId: run.local_output_suite_id };
  }
  if (
    provider === 'azure' &&
    run.azure_output_project &&
    run.azure_output_plan_id &&
    run.azure_output_suite_id
  ) {
    return {
      provider,
      project: run.azure_output_project,
      planId: run.azure_output_plan_id,
      suiteId: run.azure_output_suite_id,
    };
  }
  if (
    provider === 'testrail' &&
    run.testrail_project &&
    run.testrail_section_id != null
  ) {
    return {
      provider,
      projectId: run.testrail_project,
      sectionId: run.testrail_section_id,
    };
  }
  if (provider === 'zephyr' && run.zephyr_output_project_key) {
    return {
      provider,
      projectKey: run.zephyr_output_project_key,
      folderId: run.zephyr_output_folder_id ?? null,
    };
  }
  if (provider === 'xray' && run.xray_output_project_key) {
    if (run.xray_output_test_set_id) {
      return {
        provider,
        projectKey: run.xray_output_project_key,
        testSetId: run.xray_output_test_set_id,
      };
    }
    if (run.xray_output_test_plan_id) {
      return {
        provider,
        projectKey: run.xray_output_project_key,
        testPlanId: run.xray_output_test_plan_id,
      };
    }
    return {
      provider,
      projectKey: run.xray_output_project_key,
      folderPath: run.xray_output_folder_path ?? null,
    };
  }
  return null;
}

function groupsForAzure(suites: TestSuite[]): {
  planId: string;
  planName: string;
  suites: TestSuite[];
}[] {
  const groups = new Map<
    string,
    { planId: string; planName: string; suites: TestSuite[] }
  >();
  for (const suite of suites) {
    if (suite.source !== 'azure' || !suite.planId) continue;
    const existing = groups.get(suite.planId);
    if (existing) existing.suites.push(suite);
    else {
      groups.set(suite.planId, {
        planId: suite.planId,
        planName: suite.planName ?? `Plan ${suite.planId}`,
        suites: [suite],
      });
    }
  }
  return [...groups.values()];
}

/** Props of the destination picker. */
interface PublicationDestinationPickerProps {
  run: Run;
  value: GeneratedCasePublicationDestination | null;
  onChange: (value: GeneratedCasePublicationDestination | null) => void;
}

/**
 * Where approved cases go, with the badge of the connector that destination depends on, so a
 * missing or broken connection shows before the reviewer tries to publish.
 */
export function PublicationDestinationPicker(
  props: PublicationDestinationPickerProps,
): JSX.Element {
  if (!runOutputSource(props.run))
    return (
      <UnconfiguredDestination
        key={`${props.run.id}:${props.run.project_id}`}
        {...props}
      />
    );
  const connector = connectorForSource(runOutputSource(props.run));
  if (!connector) return <DestinationFields {...props} />;
  return (
    <div className="flex items-end gap-2">
      <ConnectorBadge
        provider={connector}
        projectId={props.run.project_id ?? undefined}
        className="mb-2"
      />
      <DestinationFields {...props} />
    </div>
  );
}

type PublicationProvider = NonNullable<ReturnType<typeof runOutputSource>>;

/** Only a destination-free run may choose its provider during review. */
export function availablePublicationProviders(
  project?: Project,
): { value: PublicationProvider; label: string }[] {
  const names: Record<string, string> = {
    testrail: 'TestRail',
    zephyr: 'Zephyr Scale',
    azure: 'Azure DevOps',
    xray: 'Xray',
  };
  const options: { value: PublicationProvider; label: string }[] = [
    { value: 'own-tms', label: 'Quality' },
  ];
  for (const link of project?.source_links ?? []) {
    if (
      Object.hasOwn(names, link.source) &&
      link.external_project_id.trim() &&
      !options.some((option) => option.value === link.source)
    )
      options.push({
        value: link.source as PublicationProvider,
        label: names[link.source]!,
      });
  }
  return options;
}

function UnconfiguredDestination({
  run,
  value,
  onChange,
}: PublicationDestinationPickerProps): JSX.Element {
  const project = useQuery(projectQueryOptions(run.project_id ?? ''));
  const currentProject =
    project.data?.id === run.project_id ? project.data : undefined;
  const options = availablePublicationProviders(currentProject);
  const [selectedChoice, setChoice] = useState<PublicationProvider | null>(
    null,
  );
  const choice = selectedChoice ?? value?.provider ?? null;
  const selectedProvider =
    choice && options.some((option) => option.value === choice) ? choice : null;
  useEffect(() => {
    if (
      choice &&
      currentProject &&
      !options.some((option) => option.value === choice)
    ) {
      setChoice(null);
      onChange(null);
    }
  }, [choice, currentProject, onChange]);
  const link = currentProject?.source_links.find(
    (item) => item.source === selectedProvider,
  )?.external_project_id;
  useEffect(() => {
    if (!currentProject || !value || value.provider === 'own-tms') return;
    const coordinate =
      value.provider === 'testrail'
        ? value.projectId
        : value.provider === 'azure'
          ? value.project
          : value.projectKey;
    const configured = currentProject.source_links.find(
      (item) => item.source === value.provider,
    )?.external_project_id;
    if (coordinate !== configured) onChange(null);
  }, [currentProject, value, onChange]);
  const targetRun = {
    ...run,
    ...(selectedProvider === 'testrail' ? { testrail_project: link } : {}),
    ...(selectedProvider === 'azure' ? { azure_output_project: link } : {}),
    ...(selectedProvider === 'zephyr'
      ? { zephyr_output_project_key: link }
      : {}),
    ...(selectedProvider === 'xray' ? { xray_output_project_key: link } : {}),
  } as Run;
  return (
    <div className="grid w-full min-w-0 gap-2">
      <DestinationSelect
        label="Save cases to"
        value={selectedProvider ?? NONE}
        options={options}
        onChange={(provider) => {
          const selected = options.find((option) => option.value === provider);
          setChoice(selected?.value ?? null);
          onChange(null);
        }}
      />
      {project.isError && (
        <p role="alert" className="text-xs text-destructive">
          External destinations could not be loaded. Save in Quality, or reopen
          review to retry the project connection.
        </p>
      )}
      {selectedProvider && connectorForSource(selectedProvider) && (
        <ConnectorBadge
          provider={connectorForSource(selectedProvider)!}
          projectId={run.project_id ?? undefined}
        />
      )}
      {selectedProvider && (
        <DestinationFields
          key={`${run.id}:${run.project_id}:${selectedProvider}:${link ?? ''}`}
          run={targetRun}
          value={value}
          onChange={onChange}
          providerOverride={selectedProvider}
          storeScope={
            currentProject
              ? localStoreWorkspaceId(currentProject)
              : (run.project_id ?? '')
          }
        />
      )}
    </div>
  );
}

function DestinationFields({
  run,
  value,
  onChange,
  providerOverride,
  storeScope,
}: PublicationDestinationPickerProps & {
  providerOverride?: PublicationProvider;
  storeScope?: string;
}): JSX.Element {
  const provider = providerOverride ?? runOutputSource(run);
  const projectId = run.project_id ?? '';
  const testRailProjectId = provider === 'testrail' ? projectId : '';
  const [testRailSuiteId, setTestRailSuiteId] = useState<number>();
  /**
   * The first TestRail section the picker receives (the parent may assign it after mount). Until a
   * suite is picked, the sections come from its suite. Latched once so later picks keep the query key.
   */
  const [testRailSectionHint, setTestRailSectionHint] = useState<number>();
  if (
    testRailSectionHint === undefined &&
    value?.provider === 'testrail' &&
    value.sectionId
  ) {
    setTestRailSectionHint(value.sectionId);
  }
  /** The default-suite read lists the suites; it stays cached while another suite's sections load. */
  const testRailSuites = useQuery(
    testRailSectionsQueryOptions(testRailProjectId),
  );
  const sections = useQuery(
    testRailSectionsQueryOptions(
      testRailProjectId,
      undefined,
      testRailSuiteId,
      testRailSuiteId === undefined ? testRailSectionHint : undefined,
    ),
  );
  const zephyrFolders = useQuery(
    zephyrFoldersQueryOptions(provider === 'zephyr' ? projectId : ''),
  );
  const xrayFolders = useQuery(
    xrayFoldersQueryOptions(provider === 'xray' ? projectId : ''),
  );
  const suites = useQuery({
    ...testSuitesQueryOptions(projectId || undefined),
    enabled: (provider === 'azure' || provider === 'xray') && !!projectId,
  });
  /** The store accepts the project id as its workspace scope, so the run's project is enough. */
  const localSuites = useQuery(
    localSuitesQueryOptions(
      provider === 'own-tms' ? (storeScope ?? projectId) : undefined,
    ),
  );

  const azureGroups = useMemo(
    () => groupsForAzure(suites.data ?? []),
    [suites.data],
  );
  const selectedAzurePlan = value?.provider === 'azure' ? value.planId : NONE;
  const azureSuites =
    azureGroups.find((group) => group.planId === selectedAzurePlan)?.suites ??
    [];
  const orderedZephyr = useMemo(
    () => orderZephyrFolders(zephyrFolders.data ?? []),
    [zephyrFolders.data],
  );
  const orderedXray = useMemo(
    () =>
      [...(xrayFolders.data ?? [])]
        .sort((first, second) => first.path.localeCompare(second.path))
        .map((folder) => ({
          ...folder,
          depth: folder.path.split('/').filter(Boolean).length - 1,
        })),
    [xrayFolders.data],
  );
  const xrayContainers = (suites.data ?? []).filter(
    (suite) => suite.source === 'xray' && suite.containerKind,
  );

  if (!provider) {
    return (
      <p role="alert" className="text-[11px] text-destructive">
        This run has no configured output destination.
      </p>
    );
  }

  if (provider === 'own-tms') {
    return (
      <QualitySuiteDestination
        key={`${run.id}:${projectId}:${storeScope ?? projectId}`}
        run={run}
        value={value}
        onChange={onChange}
        storeScope={storeScope ?? projectId}
        suites={localSuites}
      />
    );
  }

  if (provider === 'azure') {
    const project = run.azure_output_project;
    return (
      <div className="grid w-full min-w-0 grid-cols-2 gap-2 sm:min-w-[320px]">
        <DestinationSelect
          label="Test Plan"
          value={selectedAzurePlan}
          loading={suites.isLoading}
          error={suites.isError}
          options={azureGroups.map((group) => ({
            value: group.planId,
            label: group.planName,
          }))}
          onChange={(planId) => {
            const group = azureGroups.find((item) => item.planId === planId);
            onChange(
              project && group
                ? {
                    provider,
                    project,
                    planId,
                    suiteId: '',
                    planName: group.planName,
                  }
                : null,
            );
          }}
        />
        <DestinationSelect
          label="Test Suite"
          value={value?.provider === 'azure' ? value.suiteId || NONE : NONE}
          disabled={!project || selectedAzurePlan === NONE}
          options={azureSuites.map((suite) => ({
            value: suite.id,
            label: suite.name,
          }))}
          onChange={(suiteId) => {
            const suite = azureSuites.find((item) => item.id === suiteId);
            const group = azureGroups.find(
              (item) => item.planId === selectedAzurePlan,
            );
            onChange(
              project && suite && group
                ? {
                    provider,
                    project,
                    planId: group.planId,
                    suiteId,
                    planName: group.planName,
                    suiteName: suite.name,
                  }
                : null,
            );
          }}
        />
      </div>
    );
  }

  if (provider === 'testrail') {
    const tree = sections.data;
    const sectionSelect = (
      <DestinationSelect
        label="Section"
        value={value?.provider === 'testrail' ? String(value.sectionId) : NONE}
        loading={sections.isLoading}
        error={sections.isError}
        options={(tree?.sections ?? []).map((section) => ({
          value: String(section.id),
          label: `${'  '.repeat(section.depth)}${section.name}`,
        }))}
        onChange={(sectionId) => {
          const section = tree?.sections.find(
            (item) => item.id === Number(sectionId),
          );
          onChange(
            run.testrail_project && section
              ? {
                  provider,
                  projectId: run.testrail_project,
                  sectionId: section.id,
                  sectionName: section.name,
                }
              : null,
          );
        }}
      />
    );
    const suiteList = testRailSuites.data;
    if ((suiteList?.suites.length ?? 0) < 2) return sectionSelect;
    return (
      <div className="grid w-full min-w-0 grid-cols-2 gap-2 sm:min-w-[320px]">
        <DestinationSelect
          label="Suite"
          value={String(testRailSuiteId ?? tree?.suiteId ?? NONE)}
          options={(suiteList?.suites ?? []).map((suite) => ({
            value: String(suite.id),
            label: suite.name,
          }))}
          onChange={(suiteId) => {
            setTestRailSuiteId(Number(suiteId));
            onChange(null);
          }}
        />
        {sectionSelect}
      </div>
    );
  }

  if (provider === 'zephyr') {
    return (
      <DestinationSelect
        label="Folder"
        value={
          value?.provider === 'zephyr'
            ? (value.folderId ?? ROOT)
            : providerOverride
              ? NONE
              : ROOT
        }
        loading={zephyrFolders.isLoading}
        error={zephyrFolders.isError}
        options={[
          { value: ROOT, label: 'Project root' },
          ...orderedZephyr.map((folder) => ({
            value: String(folder.id),
            label: `${'  '.repeat(folder.depth)}${folder.name}`,
          })),
        ]}
        onChange={(folderId) => {
          const folder = orderedZephyr.find(
            (item) => item.id === Number(folderId),
          );
          onChange(
            run.zephyr_output_project_key
              ? {
                  provider,
                  projectKey: run.zephyr_output_project_key,
                  folderId: folderId === ROOT ? null : folderId,
                  ...(folder ? { folderName: folder.name } : {}),
                }
              : null,
          );
        }}
      />
    );
  }

  const currentMode =
    value?.provider === 'xray' && value.testSetId !== undefined
      ? 'testSet'
      : value?.provider === 'xray' && value.testPlanId !== undefined
        ? 'testPlan'
        : 'folder';
  const xrayOptions =
    currentMode === 'folder'
      ? [
          { value: ROOT, label: 'Project root' },
          ...orderedXray.map((folder) => ({
            value: folder.path,
            label: `${'  '.repeat(folder.depth)}${folder.name}`,
          })),
        ]
      : xrayContainers
          .filter((suite) => suite.containerKind === currentMode)
          .map((suite) => ({ value: suite.id, label: suite.name }));
  const xrayValue =
    value?.provider !== 'xray'
      ? NONE
      : currentMode === 'testSet'
        ? value.testSetId || NONE
        : currentMode === 'testPlan'
          ? value.testPlanId || NONE
          : value.folderPath || ROOT;

  return (
    <div className="grid w-full min-w-0 grid-cols-2 gap-2 sm:min-w-[320px]">
      <DestinationSelect
        label="Destination type"
        value={currentMode}
        options={[
          { value: 'folder', label: 'Repository folder' },
          { value: 'testSet', label: 'Test Set' },
          { value: 'testPlan', label: 'Test Plan' },
        ]}
        onChange={(mode) => {
          if (!run.xray_output_project_key) return onChange(null);
          onChange({
            provider,
            projectKey: run.xray_output_project_key,
            folderPath: mode === 'folder' ? null : undefined,
            testSetId: mode === 'testSet' ? null : undefined,
            testPlanId: mode === 'testPlan' ? null : undefined,
          });
        }}
      />
      <DestinationSelect
        label={
          currentMode === 'folder'
            ? 'Folder'
            : currentMode === 'testSet'
              ? 'Test Set'
              : 'Test Plan'
        }
        value={xrayValue}
        loading={xrayFolders.isLoading || suites.isLoading}
        error={xrayFolders.isError || suites.isError}
        options={xrayOptions}
        onChange={(id) => {
          if (!run.xray_output_project_key) return onChange(null);
          const selected = xrayContainers.find((suite) => suite.id === id);
          onChange({
            provider,
            projectKey: run.xray_output_project_key,
            folderPath:
              currentMode === 'folder' ? (id === ROOT ? null : id) : undefined,
            testSetId: currentMode === 'testSet' ? id : undefined,
            testPlanId: currentMode === 'testPlan' ? id : undefined,
            ...(selected ? { label: selected.name } : {}),
          });
        }}
      />
    </div>
  );
}

/** Creation is explicitly requested and scoped independently from approving a publication batch. */
function QualitySuiteDestination({
  run,
  value,
  onChange,
  storeScope,
  suites,
}: PublicationDestinationPickerProps & {
  storeScope: string;
  suites: {
    data?: { id: string; name: string }[];
    isLoading: boolean;
    isError: boolean;
  };
}): JSX.Element {
  const createSuite = useCreateLocalSuite(storeScope);
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);
  const [createdSuite, setCreatedSuite] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const knownSuites = [...(suites.data ?? [])];
  if (
    createdSuite &&
    !knownSuites.some((suite) => suite.id === createdSuite.id)
  )
    knownSuites.push(createdSuite);
  const inFlight = useRef(false);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const selection = JSON.stringify([
    run.id,
    run.project_id,
    storeScope,
    value,
    name,
  ]);
  const latestSelection = useRef(selection);
  latestSelection.current = selection;
  async function create(): Promise<void> {
    if (inFlight.current) {
      notify.error('Suite creation is already in progress.');
      return;
    }
    if (!name.trim()) {
      notify.error('Enter a name for the new Quality suite.');
      return;
    }
    if (!storeScope) {
      notify.error('Select a project before creating a Quality suite.');
      return;
    }
    let authority: ReturnType<typeof captureApiRequestAuthority> | undefined;
    try {
      authority = captureApiRequestAuthority(
        () => mounted.current && latestSelection.current === selection,
      );
      inFlight.current = true;
      setCreating(true);
      const suite = await createSuite.mutateAsync({
        name: name.trim(),
        requestAuthority: authority,
      });
      authority.assertCurrent();
      setCreatedSuite(suite);
      onChange({
        provider: 'own-tms',
        suiteId: suite.id,
        suiteName: suite.name,
      });
      setName('');
    } catch (error) {
      if (mounted.current && latestSelection.current === selection)
        notify.error(
          error instanceof Error
            ? error.message
            : 'Could not create the Quality suite. Try again.',
        );
    } finally {
      authority?.release();
      inFlight.current = false;
      if (mounted.current) setCreating(false);
    }
  }
  return (
    <div className="grid min-w-0 gap-2">
      <DestinationSelect
        label="Save to test suite"
        value={value?.provider === 'own-tms' ? value.suiteId : NONE}
        loading={suites.isLoading}
        error={suites.isError}
        options={knownSuites.map((suite) => ({
          value: suite.id,
          label: suite.name,
        }))}
        onChange={(suiteId) => {
          const suite = knownSuites.find((item) => item.id === suiteId);
          onChange(
            suite
              ? {
                  provider: 'own-tms',
                  suiteId: suite.id,
                  suiteName: suite.name,
                }
              : null,
          );
        }}
      />
      {!suites.isLoading && !suites.isError && !knownSuites.length && (
        <p className="text-xs text-muted-foreground">
          No Quality suites yet. Create one below to save these cases.
        </p>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <div className="min-w-0 flex-1">
          <Label htmlFor="publication-new-suite" className="text-[11px]">
            New Quality suite name
          </Label>
          <Input
            id="publication-new-suite"
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
        </div>
        <Button
          type="button"
          variant="brand-outline"
          onClick={() => void create()}
        >
          {creating ? 'Creating suite…' : 'Create suite'}
        </Button>
      </div>
    </div>
  );
}

/** One labelled destination picker; the trigger fills its grid cell instead of shrinking to its text. */
function DestinationSelect({
  label,
  value,
  options,
  onChange,
  loading = false,
  error = false,
  disabled = false,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  loading?: boolean;
  error?: boolean;
  disabled?: boolean;
}): JSX.Element {
  const id = `publication-${label.toLowerCase().replaceAll(' ', '-')}`;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <Label htmlFor={id} className="text-[11px]">
          {label}
        </Label>
        <GenerationHelp
          label={`About ${label}`}
          text={
            label === 'Save to test suite'
              ? 'The existing test suite in Quality where selected cases will be saved. This does not choose an environment or start execution.'
              : 'Where selected cases will be saved in the destination test management system. Changing this selection does not regenerate the cases.'
          }
        />
      </div>
      {loading ? (
        <span className="flex h-8 items-center text-[10px] text-muted-foreground">
          Loading…
        </span>
      ) : error ? (
        <span className="flex h-8 items-center text-[10px] text-destructive">
          Failed to load destinations
        </span>
      ) : (
        <Select
          value={value || NONE}
          onValueChange={(next) => {
            if (!disabled) onChange(next);
          }}
        >
          <SelectTrigger
            id={id}
            className="h-8 w-full text-[11px]"
            onPointerDown={(event) => {
              if (disabled) {
                event.preventDefault();
                notify.error(
                  'Select a Test Plan before choosing its Test Suite.',
                );
              }
            }}
            onKeyDown={(event) => {
              if (
                disabled &&
                ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)
              ) {
                event.preventDefault();
                notify.error(
                  'Select a Test Plan before choosing its Test Suite.',
                );
              }
            }}
          >
            <SelectValue placeholder={`Select ${label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {value === NONE ? (
              <SelectItem value={NONE} disabled className="text-[11px]">
                Select {label.toLowerCase()}
              </SelectItem>
            ) : null}
            {options.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                className="text-[11px]"
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
    </div>
  );
}
